import { useState } from "react";
import { Modal } from "react-bootstrap";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import comingSoonEvent from "../../assets/lottie-files/comingSoonEvent.lottie";

const TAG_COLORS: Record<string, { bg: string; color: string }> = {
  Multicultural: { bg: "#fff3cd", color: "#7a5c00" },
  Food: { bg: "#d4edda", color: "#1a5c32" },
  Music: { bg: "#cce5ff", color: "#004085" },
  Crafts: { bg: "#fde8e8", color: "#8b2020" },
  Workshop: { bg: "#ede0f8", color: "#5a1e8a" },
  Holiday: { bg: "#ffd6e0", color: "#8b0000" },
  Vintage: { bg: "#f5e6d0", color: "#7a4a00" },
  Art: { bg: "#f5d0f0", color: "#7a0070" },
  Beer: { bg: "#fff3d0", color: "#8a5c00" },
  Books: { bg: "#d0e8ff", color: "#003580" },
  Literature: { bg: "#d0e8ff", color: "#003580" },
  Fitness: { bg: "#d0ffd8", color: "#005c10" },
  Yoga: { bg: "#d0ffd8", color: "#005c10" },
  Animals: { bg: "#ffe8d0", color: "#7a3000" },
  "LGBTQ+": { bg: "#ffd0ee", color: "#7a0054" },
  Cultural: { bg: "#fff3cd", color: "#7a5c00" },
  Shopping: { bg: "#d0f0ff", color: "#005080" },
  Handmade: { bg: "#fde8e8", color: "#8b2020" },
  "Family-Friendly": { bg: "#d0ffd8", color: "#005c10" },
  Chocolate: { bg: "#f0e0d0", color: "#5a2500" },
  Cocktail: { bg: "#ffe8d0", color: "#7a3000" },
  Mocktail: { bg: "#ffe8d0", color: "#7a3000" },
  DIY: { bg: "#e0f0ff", color: "#003580" },
  Tools: { bg: "#e0f0ff", color: "#003580" },
  Easter: { bg: "#f0e8ff", color: "#5a008a" },
  "Beginner-Friendly": { bg: "#d0ffd8", color: "#005c10" },
  Ramadan: { bg: "#d0f0e8", color: "#005040" },
  Evening: { bg: "#e8d0f8", color: "#3d0080" },
  Prints: { bg: "#f5d0f0", color: "#7a0070" },
  Dance: { bg: "#cce5ff", color: "#004085" },
  Gifts: { bg: "#ffd6e0", color: "#8b0000" },
  Collectibles: { bg: "#f5e6d0", color: "#7a4a00" },
};

type EventModalProps = {
  show: boolean;
  onHide: () => void;
  event: {
    id: string;
    title: string;
    description: string;
    dates: string[];
    time: string;
    image?: { asset: { _ref: string } };
    imageUrl?: string;
    ticketImage?: { asset: { _ref: string } };
    admission?: string;
    highlights?: string[];
    tags?: string[];
    tickets?: string;
    paymentLink?: string;
    organizer?: {
      name?: string;
      email?: string;
      phone?: string;
      website?: string;
    };
  };
};

export default function EventModal({ show, onHide, event }: EventModalProps) {
  const [showAllDates, setShowAllDates] = useState(false);

  const formatDate = (isoDate: string) => {
    const [y, m, d] = isoDate.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const images = (require as any).context("../../assets", true);
  const resolveImageSrc = (path: string | undefined): string | undefined => {
    if (!path || path.length === 0) return undefined;
    const trimmed = path.trim();
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("/")
    ) {
      return trimmed;
    }
    try {
      const relative = trimmed.replace(/^.*\/assets\//, "./");
      return images(relative).default ?? images(relative);
    } catch {
      return undefined;
    }
  };

  const imgSrc = resolveImageSrc(event.imageUrl);
  const sortedDates = [...event.dates].sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      aria-labelledby="event-modal"
      centered
      scrollable
    >
      {/* Borderless header — just the close button */}
      <Modal.Header
        closeButton
        style={{ border: "none", paddingBottom: 0, paddingTop: "12px" }}
      />

      <Modal.Body style={{ padding: 0, overflowY: "auto" }}>
        {/* Hero Image */}
        <div
          style={{
            width: "100%",
            height: "260px",
            overflow: "hidden",
            backgroundColor: "#f3f3f3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!imgSrc ? (
            <DotLottieReact
              src={comingSoonEvent}
              loop
              autoplay
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <img
              src={imgSrc}
              alt={event.title}
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }}
            />
          )}
        </div>

        {/* Content */}
        <div style={{ padding: "20px 24px 24px" }}>
          {/* Title */}
          <h4
            id="event-modal"
            style={{
              fontFamily: "Yanone Kaffeesatz",
              fontWeight: "bold",
              fontSize: "1.6rem",
              marginBottom: "10px",
            }}
          >
            {event.title}
          </h4>

          {/* Tags */}
          {event.tags && event.tags.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                marginBottom: "18px",
              }}
            >
              {event.tags.map((tag) => {
                const c = TAG_COLORS[tag] ?? { bg: "#e8e8e8", color: "#444" };
                return (
                  <span
                    key={tag}
                    style={{
                      padding: "3px 12px",
                      borderRadius: "20px",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      backgroundColor: c.bg,
                      color: c.color,
                    }}
                  >
                    {tag}
                  </span>
                );
              })}
            </div>
          )}

          {/* Info strip — Date / Time / Admission */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              marginBottom: "20px",
              paddingBottom: "20px",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <InfoChip
              icon="📅"
              label="Date"
              value={formatDate(sortedDates[0])}
              extra={
                event.dates.length > 1
                  ? `+${event.dates.length - 1} more date${event.dates.length > 2 ? "s" : ""}`
                  : undefined
              }
            />
            <InfoChip icon="🕐" label="Time" value={event.time} />
            {event.admission && (
              <InfoChip
                icon={event.admission === "Free" ? "🎉" : "🎟️"}
                label="Admission"
                value={event.admission}
                highlight={event.admission !== "Free"}
                green={event.admission === "Free"}
              />
            )}
          </div>

          {/* Highlights */}
          {event.highlights && event.highlights.length > 0 && (
            <div style={{ marginBottom: "20px" }}>
              <SectionTitle>Highlights</SectionTitle>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: "8px 16px",
                }}
              >
                {event.highlights.map((h) => (
                  <div
                    key={h}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      fontSize: "0.92rem",
                    }}
                  >
                    <span
                      style={{
                        color: "#D46540",
                        fontWeight: "bold",
                        flexShrink: 0,
                        marginTop: "1px",
                      }}
                    >
                      ✓
                    </span>
                    <span>{h}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* About */}
          <div style={{ marginBottom: "20px" }}>
            <SectionTitle>About This Event</SectionTitle>
            <p style={{ lineHeight: "1.65", fontSize: "0.95rem", color: "#333", margin: 0 }}>
              {event.description}
            </p>
          </div>

          {/* Multiple dates */}
          {event.dates.length > 1 && (
            <div style={{ marginBottom: "20px" }}>
              <SectionTitle>All Dates</SectionTitle>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                {(() => {
                  const visible = showAllDates ? sortedDates : sortedDates.slice(0, 6);
                  return (
                    <>
                      {visible.map((d, idx) => (
                        <span
                          key={`${d}-${idx}`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "5px 12px",
                            borderRadius: "16px",
                            background:
                              "linear-gradient(90deg, rgba(23,160,219,0.1), rgba(13,110,253,0.1))",
                            color: "#0d6efd",
                            fontWeight: 600,
                            fontSize: "0.82rem",
                            border: "1px solid rgba(13,110,253,0.2)",
                          }}
                        >
                          {formatDate(d)}
                        </span>
                      ))}
                      {sortedDates.length > 6 && !showAllDates && (
                        <button
                          onClick={() => setShowAllDates(true)}
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "#0d6efd",
                            fontWeight: 700,
                            cursor: "pointer",
                            fontSize: "0.85rem",
                          }}
                        >
                          +{sortedDates.length - 6} more
                        </button>
                      )}
                      {showAllDates && sortedDates.length > 6 && (
                        <button
                          onClick={() => setShowAllDates(false)}
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "#0d6efd",
                            fontWeight: 700,
                            cursor: "pointer",
                            fontSize: "0.85rem",
                          }}
                        >
                          Show less
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
              <div style={{ color: "#888", fontSize: "0.82rem" }}>
                Same time for all dates
              </div>
            </div>
          )}

          {/* QR code tickets */}
          {event.tickets && resolveImageSrc(event.tickets) && (
            <div style={{ marginBottom: "20px" }}>
              <SectionTitle>Scan to Get Tickets</SectionTitle>
              <img
                src={resolveImageSrc(event.tickets)}
                alt="Tickets QR code"
                style={{
                  width: "160px",
                  height: "160px",
                  objectFit: "contain",
                  borderRadius: "10px",
                  border: "1px solid #e0e0e0",
                }}
              />
            </div>
          )}

          {/* Payment CTA */}
          {event.paymentLink && (
            <div
              style={{
                marginBottom: "20px",
                padding: "18px 22px",
                borderRadius: "12px",
                background:
                  "linear-gradient(135deg, #D46540 0%, #e8855f 50%, #f0a080 100%)",
                boxShadow: "0 4px 16px rgba(212,101,64,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "16px",
                flexWrap: "wrap",
              }}
            >
              <div>
                <div
                  style={{
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    fontFamily: "Yanone Kaffeesatz",
                    letterSpacing: "0.5px",
                    marginBottom: "2px",
                  }}
                >
                  🎟️ Secure Your Spot
                </div>
                <div style={{ color: "rgba(255,255,255,0.88)", fontSize: "0.88rem" }}>
                  Tickets available online — don't miss out!
                </div>
              </div>
              <a
                href={event.paymentLink}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "inline-block",
                  backgroundColor: "#fff",
                  color: "#D46540",
                  fontWeight: "bold",
                  fontSize: "0.95rem",
                  padding: "9px 22px",
                  borderRadius: "30px",
                  textDecoration: "none",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  whiteSpace: "nowrap",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
                }}
              >
                Buy Tickets →
              </a>
            </div>
          )}

          {/* Organizer */}
          {event.organizer && (
            <div
              style={{
                padding: "14px 18px",
                border: "1px solid #eeeeee",
                borderRadius: "10px",
                background:
                  "linear-gradient(180deg, rgba(71,195,247,0.05), rgba(212,101,64,0.05))",
              }}
            >
              <div style={{ fontWeight: "bold", marginBottom: "4px", fontSize: "0.9rem" }}>
                Interested in joining this event?
              </div>
              <div style={{ color: "#555", marginBottom: "8px", fontSize: "0.88rem" }}>
                Contact the organizer{event.organizer.name ? `, ${event.organizer.name}` : ""}:
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                {event.organizer.email && (
                  <a
                    href={`mailto:${event.organizer.email}`}
                    style={{ color: "#0d6efd", textDecoration: "none", fontWeight: 600, fontSize: "0.9rem" }}
                  >
                    ✉️ {event.organizer.email}
                  </a>
                )}
                {event.organizer.phone && (
                  <a
                    href={`tel:${event.organizer.phone}`}
                    style={{ color: "#198754", textDecoration: "none", fontWeight: 600, fontSize: "0.9rem" }}
                  >
                    ☎️ {event.organizer.phone}
                  </a>
                )}
                {event.organizer.website && (
                  <a
                    href={event.organizer.website}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "#D46540", textDecoration: "none", fontWeight: 600, fontSize: "0.9rem" }}
                  >
                    🌐 Organizer Website
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer style={{ border: "none", paddingTop: 0 }}>
        <button
          onClick={onHide}
          style={{
            backgroundColor: "#D46540",
            color: "#fff",
            border: "none",
            padding: "9px 24px",
            borderRadius: "5px",
            fontFamily: "Yanone Kaffeesatz",
            fontWeight: "bold",
            fontSize: "1.1rem",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#B8543A";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#D46540";
          }}
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h6
      style={{
        color: "#D46540",
        fontWeight: "bold",
        marginBottom: "10px",
        fontSize: "0.95rem",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      {children}
    </h6>
  );
}

function InfoChip({
  icon,
  label,
  value,
  extra,
  highlight,
  green,
}: {
  icon: string;
  label: string;
  value: string;
  extra?: string;
  highlight?: boolean;
  green?: boolean;
}) {
  return (
    <div
      style={{
        flex: "1 1 140px",
        minWidth: "120px",
        padding: "10px 14px",
        borderRadius: "10px",
        backgroundColor: highlight
          ? "rgba(212,101,64,0.08)"
          : green
          ? "rgba(26,92,50,0.07)"
          : "#f8f8f8",
        border: `1px solid ${highlight ? "rgba(212,101,64,0.2)" : green ? "rgba(26,92,50,0.2)" : "#eeeeee"}`,
      }}
    >
      <div style={{ fontSize: "0.72rem", color: "#888", marginBottom: "2px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>
        {icon} {label}
      </div>
      <div
        style={{
          fontWeight: 700,
          fontSize: "0.9rem",
          color: highlight ? "#D46540" : green ? "#1a5c32" : "#222",
        }}
      >
        {value}
      </div>
      {extra && (
        <div
          style={{
            fontSize: "0.75rem",
            marginTop: "2px",
            padding: "1px 8px",
            borderRadius: "10px",
            display: "inline-block",
            background: "linear-gradient(90deg, #FF7E5F, #FF6A00)",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {extra}
        </div>
      )}
    </div>
  );
}
